import { useKirjaustunniste } from "./kirjaustunniste";
import { useLataus, LatausTila } from "./Lataus";
import { naytaPvm } from "./pvm";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useKohde } from "./Kohdekonteksti";
import { api, tanaan, type Kayttaja, type Kohde, type Resurssi, type Tyopaketti,
  type TuntiRivi, type TuntiYhteenveto, type TuntiAsetukset, type JohdettuTunti } from "./api";
import { Tilasana } from "./Tila";
import { Ohje } from "./Ohje";
import { t, lokaali, virheTeksti, type KaannosAvain } from "./kieli";
import { ApiError } from "./api";

/* Tuntikirjaus: henkilö × pvm × kohde/paketti × tunnit + km-korvaus + päiväraha.
   Kaikki kirjaavat omat tuntinsa; työnjohto näkee kaikki + hyväksyy.
   Korvausluvut (€/km, päiväraha) asetuksissa — talo täyttää. */

const PVRAHA: { avain: "ei" | "osa" | "koko"; nimi: KaannosAvain }[] = [
  { avain: "ei", nimi: "tk.pvraha.ei" },
  { avain: "osa", nimi: "tk.pvraha.osa" },
  { avain: "koko", nimi: "tk.pvraha.koko" },
];

/* Tehtävälajit — ehdotuksia, ei lukittu (vapaa teksti).
   T439: arvo tallentuu suomeksi (aineisto kertyy asiakkaan kielellä, päätös 105); englanninkielinen näkee käännöksen
   ehdotuksen selitteenä (option label). Tunnisteiksi muunto on päätöksen 106 kaltainen oma eränsä. */
const TEHTAVAT: [string, KaannosAvain][] = [["Hitsaus", "tk.teht.hitsaus"], ["Esivalmistus", "tk.teht.esivalmistus"],
  ["Dokumentointi", "tk.teht.dokumentointi"], ["Korjaus/isometri", "tk.teht.korjaus"], ["Punch", "tk.teht.punch"],
  ["NDT", "tk.teht.ndt"], ["Kokoonpano", "tk.teht.kokoonpano"], ["Materiaali", "tk.teht.materiaali"], ["Muu", "tk.teht.muu"]];

const eur = (n: number) => n.toLocaleString(lokaali(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const h = (n: number) => n.toLocaleString(lokaali(), { maximumFractionDigits: 1 });
const kkAlku = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
// Oletuspäivä paikallisesta kalenterista (api.ts tanaan). Aiempi toISOString antoi klo 00–03 edellisen
// päivän, joka tallentui uuteen tuntikirjaukseen ja rajasi tämän päivän rivit pois suodattimesta (mittaus 17.9.2026).

export function Tuntikirjaus({ kayttaja }: { kayttaja: Kayttaja }) {
  const saaKaikki = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r));
  // Toni 16.10.2026 klo 04.25 kohdat 1 ja 2: palkka-aineiston linkki ja eurot vain palkkaoikeudella (sama ehto kuin API).
  const saaRahat = kayttaja.oikeudet.includes("arkaluonteiset_henkilotiedot");
  const onPaakayttaja = kayttaja.roolit.includes("paakayttaja");

  const [alku, setAlku] = useState(kkAlku());
  const [loppu, setLoppu] = useState(tanaan());
  const [henkilo, setHenkilo] = useState<number | "">("");
  const [rivit, setRivit] = useState<TuntiRivi[]>([]);
  const [yhteenveto, setYhteenveto] = useState<TuntiYhteenveto[]>([]);
  const [johdetut, setJohdetut] = useState<JohdettuTunti[]>([]);
  // Jakso jolta johdetut on haettu: ruudun luku ja jakson kentät voivat hetken erota (kävely odottaa tätä).
  const [johdetutJakso, setJohdetutJakso] = useState("");
  const [resurssit, setResurssit] = useState<Resurssi[]>([]);
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [uusiAuki, setUusiAuki] = useState(false);
  const [asetuksetAuki, setAsetuksetAuki] = useState(false);

  // 6.10.2026: vastaukset voivat tulla eri järjestyksessä kuin pyynnöt. Mitattu demon lokista: jakson kahden kentän
  // asetus laukaisi kaksi hakua (väli- ja lopullinen jakso), ja välijakson vastaus valmistui 2 ms myöhemmin — ruutu näytti
  // vanhan jakson luvut uuden jakson otsikon alla. Vain viimeisimmän haun vastaus hyväksytään.
  const viimeisin = useRef(0);
  /* Toni 16.10.2026 klo 04.45 kohta 2: lista ja yhteenveto suodattuvat yläpalkin kohteen mukaan kuten muut kohdenäkymät;
     «Kaikki kohteet» näyttää kaikki (työntekijä kirjaa viikossa usealle kohteelle). Ennen yläpalkin latautumista ei haeta,
     jottei ruutu näytä ensin kaikkia ja sitten kohteen (sama vika kuin Etenemässä T472). Johdetut tunnit (hitsauslokista)
     eivät tunne kohdesuodatinta, ja ne näkyvät koko jaksolta. */
  const { valittu: ylaKohde, kaytossa: ylapalkki } = useKohde();
  const kohdeSuodatin = typeof ylaKohde === "number" ? ylaKohde : undefined;
  const ylapalkkiValmis = !ylapalkki || ylaKohde !== undefined;
  const lataa = useCallback(() => {
    if (!ylapalkkiValmis) return;
    const n = ++viimeisin.current;
    const tuore = () => n === viimeisin.current;
    api.tuntikirjaukset(alku, loppu, { ...(henkilo !== "" ? { henkilo_id: henkilo } : {}), ...(kohdeSuodatin !== undefined ? { kohde_id: kohdeSuodatin } : {}) })
      .then((r) => { if (tuore()) setRivit(r); }).catch(() => { if (tuore()) setRivit([]); });
    api.tuntiYhteenveto(alku, loppu, kohdeSuodatin).then((r) => { if (tuore()) setYhteenveto(r); }).catch(() => { if (tuore()) setYhteenveto([]); });
    api.johdetutTunnit(alku, loppu, henkilo !== "" ? henkilo : undefined)
      .then((r) => { if (tuore()) { setJohdetut(r); setJohdetutJakso(`${alku}..${loppu}`); } })
      .catch(() => { if (tuore()) setJohdetut([]); });
  }, [alku, loppu, henkilo, kohdeSuodatin, ylapalkkiValmis]);

  useEffect(() => { lataa(); }, [lataa]);
  const klKohteet = useLataus();
  useEffect(() => {
    klKohteet.seuraa(Promise.all([api.kohteet().then(setKohteet),
      saaKaikki ? api.resurssit().then((r) => setResurssit(r.filter((x) => !x.nimeamaton))) : Promise.resolve()]));
  }, [saaKaikki, klKohteet.kerta]);

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.tuntikirjaus")}</h1></div>
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet_henkilot" />

      {/* Ohje ylös (Toni 24.9.2026). «Kuka» kuvaa reittien portit: tuntiSaaKaikki, vaadiRooli ja palkka-aineiston
          arkaluonteisuusoikeus (routes.ts /api/tuntikirjaus*). Portin muuttuessa tämä teksti muuttuu samassa commitissa. */}
      <Ohje
        kuka={<>{t("tk.ohje.kuka")}</>}
        luvut={<>{t("tk.ohje.luvut")}</>} />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", margin: "0 0 14px" }}>
        <label className="dim" >{t("tk.alkaen")}<br /><input type="date" value={alku} onChange={(e) => setAlku(e.target.value)} /></label>
        <label className="dim" >{t("tk.asti")}<br /><input type="date" value={loppu} onChange={(e) => setLoppu(e.target.value)} /></label>
        {saaKaikki && (
          <label className="dim" >{t("tk.henkilo")}<br />
            <select value={henkilo} onChange={(e) => setHenkilo(e.target.value ? Number(e.target.value) : "")}>
              <option value="">{t("pu.kaikki")}</option>
              {resurssit.map((r) => <option key={r.id} value={r.id}>{r.lyhenne ? r.lyhenne + " · " : ""}{r.nimi}</option>)}
            </select>
          </label>
        )}
        <button onClick={() => setUusiAuki(!uusiAuki)} data-avaa-lomake="tuntikirjaus">{uusiAuki ? t("hl.sulje") : t("tk.kirjaa_tunnit")}</button>
        {onPaakayttaja && <button className="toissijainen" onClick={() => setAsetuksetAuki(!asetuksetAuki)}>{t("tk.korvausluvut")}</button>}
        {saaKaikki && saaRahat && (
          <a className="nappi nappi--kevyt" style={{ textDecoration: "none", alignSelf: "center" }}
             href={api.palkkaAineistoOsoite(alku, loppu, henkilo !== "" ? henkilo : undefined)}
             title={t("tk.palkka_title")}>
            {t("tk.palkka")}
          </a>
        )}
      </div>

      {asetuksetAuki && onPaakayttaja && <Asetukset onTallennettu={() => { setAsetuksetAuki(false); lataa(); }} />}
      {uusiAuki && (
        <UusiTunti saaKaikki={saaKaikki} resurssit={resurssit} kohteet={kohteet}
          onLuotu={() => { setUusiAuki(false); lataa(); }} />
      )}

      {/* Yhteenveto */}
      {yhteenveto.length > 0 && (
        <section className="panel" style={{ marginBottom: 16 }}>
          <div className="panel__head"><h2>{t("tk.yhteenveto")} {naytaPvm(alku)} – {naytaPvm(loppu)}</h2></div>
          <table className="tbl">
            <thead><tr><th>{t("tk.henkilo")}</th><th style={{ textAlign: "right" }}>{t("tk.tunnit")}</th><th style={{ textAlign: "right" }}>km</th>
              {saaRahat && <><th style={{ textAlign: "right" }}>{t("tk.km_korvaus")}</th><th style={{ textAlign: "right" }}>{t("tk.paivaraha")}</th><th style={{ textAlign: "right" }}>{t("tk.korvaukset")}</th></>}</tr></thead>
            <tbody>
              {yhteenveto.map((y) => (
                <tr key={y.henkilo_id}>
                  <td>{y.lyhenne ? y.lyhenne + " · " : ""}{y.nimi}</td>
                  <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{h(y.tunnit)} h</td>
                  <td className="mono" style={{ textAlign: "right" }}>{h(y.km)}</td>
                  {saaRahat && <>
                  <td className="mono" style={{ textAlign: "right" }}>{eur(y.km_korvaus ?? 0)}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{eur(y.paivaraha_eur ?? 0)}</td>
                  <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{eur((y.km_korvaus ?? 0) + (y.paivaraha_eur ?? 0))}</td>
                  </>}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Johdetut tunnit saumoista.
        *
        * ⚠ EI mitattua työaikaa. Luku on normi × saumat
        * (f_hitsausnormi, DN→h/hitsi), eli ENNUSTE siitä paljonko
        * työhön pitäisi mennä — ei havainto siitä paljonko meni.
        * Tässä luki "mitattu työ" 29.8.2026 asti, ja se on sama
        * vikaperhe kuin lahde='arvio': johdettu joka kuvataan
        * havaintona.
        *
        * Ero on koko tehokkuusmittarin edellytys: jos johdettua
        * verrataan normiin, tulos on 100 % rakenteen vuoksi.
        * Ainoa kelvollinen vertailukohta on kirjatut tunnit, ja
        * tuntikirjaus on 0 riviä kaikissa neljässä instanssissa
        * (mitattu 29.8.2026).
        *
        * Siksi Kirjatut h renderöityy viivana eikä nollana: mitattu
        * tyhjä on eri asia kuin nolla tuntia. */}
      {johdetut.length > 0 && (
        <section className="panel" style={{ marginBottom: 16 }}>
          <div className="panel__head"><h2>{t("tk.johdetut")} {naytaPvm(alku)} – {naytaPvm(loppu)}</h2>
            <span className="hint">{t("tk.johdetut_hint")}</span></div>
          <table className="tbl">
            <thead><tr><th>{t("hl.hitsaaja")}</th><th style={{ textAlign: "right" }}>{t("tk.saumoja")}</th>
              <th style={{ textAlign: "right" }}>{t("tk.arvio_h")}</th><th style={{ textAlign: "right" }}>{t("tk.kirjatut_h")}</th>
              <th style={{ textAlign: "right" }}>{t("tk.dia_tuumaa")}</th><th></th></tr></thead>
            <tbody>
              {johdetut.map((r) => (
                <tr key={r.henkilo_id}>
                  <td>{r.lyhenne ?? r.nimi}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{r.hitseja}</td>
                  <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{h(r.johdetut_h)} h</td>
                  <td className="mono" style={{ textAlign: "right" }}>{r.kirjatut_h > 0 ? h(r.kirjatut_h) + " h" : "—"}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{h(r.dia_inch)}</td>
                  <td>{r.ilman_normia > 0 && <span style={{ color: "var(--puna)", fontSize: 12 }}
                    title={t("tk.ilman_dn_title")}>⚠ {t("tk.ilman_dn", { n: r.ilman_normia })}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Toni 25.9.2026: luku kertoo mistä se tulee. Kun kirjattuja tunteja ei ole, lause sanoo sen; kun on, molemmat
              rinnakkain. Erottelu rakennetaan nyt, kun kirjattuja on nolla kaikissa instansseissa (mitattu 29.8.). */}
          {(() => {
            const arvio = johdetut.reduce((s, r) => s + r.johdetut_h, 0);
            const kirjatut = johdetut.reduce((s, r) => s + r.kirjatut_h, 0);
            return (
              <p className="dim" style={{ marginTop: 8 }} data-tuntierottelu={kirjatut > 0 ? "kirjattu" : "arvio"}
                 data-arvio={arvio.toFixed(1)} data-kirjatut={kirjatut.toFixed(1)} data-jakso={johdetutJakso}>
                <strong>{kirjatut > 0 ? t("tk.kirjattuja_arvio", { k: h(kirjatut), a: h(arvio) }) : t("tk.arvio_ei_kirjattuja")}</strong>{" "}
                {t("tk.arvio_selite")}
              </p>
            );
          })()}
        </section>
      )}

      {/* Rivit */}
      <section className="panel scroll">
        <div className="panel__head" data-tk-kohde={kohdeSuodatin ?? "kaikki"} data-tk-rivit={rivit.length}><h2>{t("tk.kirjaukset")}</h2><span className="hint">{t("tk.n_rivia", { n: rivit.length })}</span></div>
        {rivit.length === 0 && <p className="dim">{t("tk.ei_kirjauksia")}</p>}
        {rivit.length > 0 && (
          <table className="tbl" style={{ fontSize: 13 }}>
            <thead><tr>
              <th>{t("tk.pvm")}</th>{saaKaikki && <th>{t("tk.henkilo")}</th>}<th>{t("tk.kohde_paketti")}</th><th>{t("tk.tehtava")}</th>
              <th style={{ textAlign: "right" }}>{t("tk.tunnit")}</th><th style={{ textAlign: "right" }}>km</th>
              <th>{t("tk.paivaraha")}</th>{saaRahat && <th style={{ textAlign: "right" }}>€</th>}<th>{t("pu.tila")}</th><th></th>
            </tr></thead>
            <tbody>
              {rivit.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{naytaPvm(r.pvm)}</td>
                  {saaKaikki && <td>{r.lyhenne ?? r.nimi}</td>}
                  <td>{r.tp_nro ? `#${r.tp_nro} ${r.tp_nimi}` : (r.kohde ?? "—")}</td>
                  <td>{r.tehtava ?? ""}</td>
                  <td className="mono" style={{ textAlign: "right", fontWeight: 700 }}>{h(r.tunnit)}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{r.km ? h(r.km) : ""}</td>
                  <td>{r.paivaraha === "ei" ? "" : (r.paivaraha === "osa" || r.paivaraha === "koko") && t("tk.pvraha_lyhyt." + r.paivaraha as KaannosAvain)}</td>
                  {saaRahat && <td className="mono" style={{ textAlign: "right" }}>{(r.km_korvaus ?? 0) + (r.paivaraha_eur ?? 0) > 0 ? eur((r.km_korvaus ?? 0) + (r.paivaraha_eur ?? 0)) : ""}</td>}
                  <td>{r.tila === "hyvaksytty"
                    ? <Tilasana laji="kunnossa" style={{ fontSize: 12 }}>{t("tk.tila.hyvaksytty")}</Tilasana>
                    : <Tilasana laji="kunnossa" style={{ fontSize: 12 }}>{t("hl.teko.kirjattu")}</Tilasana>}</td>  {/* arvo, ei «ei mitattu»: himmeä olisi sama vika pienempänä (Toni 10.9.) */}
                  <td style={{ whiteSpace: "nowrap" }}>
                    {saaKaikki && r.tila !== "hyvaksytty" && (
                      <button className="nappi nappi--kevyt" title={t("tk.hyvaksy")}
                        onClick={() => api.muokkaaTunti(r.id, { tila: "hyvaksytty" }).then(lataa)}>✓</button>
                    )}
                    {r.tila !== "hyvaksytty" && (
                      <button className="nappi nappi--kevyt" title={t("tk.poista")}
                        onClick={() => { if (confirm(t("tk.poista_kysymys"))) api.poistaTunti(r.id).then(lataa); }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

    </>
  );
}

/* ── uusi kirjaus ─────────────────────────────────────────── */
function UusiTunti({ saaKaikki, resurssit, kohteet, onLuotu }:
  { saaKaikki: boolean; resurssit: Resurssi[]; kohteet: Kohde[]; onLuotu: () => void }) {
  const [henkiloId, setHenkiloId] = useState<number | "">("");
  const [pvm, setPvm] = useState(tanaan());
  const [kohdeId, setKohdeId] = useState<number | "">("");
  // T589 (arkkitehtuuri vaihe 1, kohdekoukku; Toni 16.10.2026 klo 03.45): oletus yläpalkin kohteesta (useKohde), vaihdettavissa.
  const { valittu: ylaKohde } = useKohde();
  useEffect(() => { if (typeof ylaKohde === "number" && kohdeId === "") setKohdeId(ylaKohde); }, [ylaKohde]);
  const [tpId, setTpId] = useState<number | "">("");
  const [paketit, setPaketit] = useState<Tyopaketti[]>([]);
  const [tunnit, setTunnit] = useState("");
  const [km, setKm] = useState("");
  const [paivaraha, setPaivaraha] = useState<"ei" | "osa" | "koko">("ei");
  const [tehtava, setTehtava] = useState("");
  const [huomiot, setHuomiot] = useState("");
  const [tallentaa, setTallentaa] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tunniste, vaihdaTunniste] = useKirjaustunniste();   // T323

  useEffect(() => {
    if (kohdeId === "") { setPaketit([]); return; }
    // Päätös 99: edellisen kohteen paketit eivät saa jäädä valikkoon.
    let elossa = true;
    setPaketit([]);
    api.tyopaketit(kohdeId).then((p) => { if (elossa) setPaketit(p); }).catch(() => { if (elossa) setPaketit([]); });
    return () => { elossa = false; };
  }, [kohdeId]);

  async function tallenna() {
    const tt = Number(tunnit.replace(",", "."));
    if (!(tt > 0)) { setVirhe(t("tk.anna_tunnit")); return; }
    setTallentaa(true); setVirhe(null);
    try {
      await api.luoTunti({
        ...(saaKaikki && henkiloId !== "" && { henkilo_id: henkiloId }),
        ...(kohdeId !== "" && { kohde_id: kohdeId }),
        ...(tpId !== "" && { tyopaketti_id: tpId }),
        kirjaustunniste: tunniste,
        pvm, tunnit: tt, km: km.trim() ? Number(km.replace(",", ".")) : 0, paivaraha,
        ...(tehtava.trim() && { tehtava: tehtava.trim() }),
        ...(huomiot.trim() && { huomiot: huomiot.trim() }),
      });
      vaihdaTunniste();
      onLuotu();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : t("tk.tallennus_virhe")); }
    finally { setTallentaa(false); }
  }

  const kentta: CSSProperties = { display: "flex", flexDirection: "column", fontSize: 11 };
  return (
    <div style={{ background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        {saaKaikki && (
          <label style={kentta}>{t("tk.henkilo")}<select value={henkiloId} onChange={(e) => setHenkiloId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{t("tk.mina")}</option>
            {resurssit.map((r) => <option key={r.id} value={r.id}>{r.lyhenne ? r.lyhenne + " · " : ""}{r.nimi}</option>)}
          </select></label>
        )}
        <label style={kentta}>{t("tk.pvm")}<input type="date" value={pvm} onChange={(e) => setPvm(e.target.value)} /></label>
        <label style={kentta}>{t("pu.kohde")}<select value={kohdeId} onChange={(e) => { setKohdeId(e.target.value ? Number(e.target.value) : ""); setTpId(""); }} data-kohdekoukku="tuntikirjaus">
          <option value="">—</option>
          {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
        </select></label>
        {paketit.length > 0 && (
          <label style={kentta}>{t("tk.tyopaketti")}<select value={tpId} onChange={(e) => setTpId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">—</option>
            {paketit.map((p) => <option key={p.id} value={p.id}>#{p.nro} {p.nimi}</option>)}
          </select></label>
        )}
        <label style={kentta}>{t("tk.tunnit")}<input value={tunnit} onChange={(e) => setTunnit(e.target.value)} inputMode="decimal" style={{ width: 70 }} placeholder="7,5" /></label>
        <label style={kentta}>km<input value={km} onChange={(e) => setKm(e.target.value)} inputMode="decimal" style={{ width: 70 }} placeholder="0" /></label>
        <label style={kentta}>{t("tk.paivaraha")}<select value={paivaraha} onChange={(e) => setPaivaraha(e.target.value as typeof paivaraha)}>
          {PVRAHA.map((p) => <option key={p.avain} value={p.avain}>{t(p.nimi)}</option>)}
        </select></label>
        <label style={kentta}>{t("tk.tehtava")}<input value={tehtava} onChange={(e) => setTehtava(e.target.value)} list="tehtavat" style={{ width: 130 }} placeholder={t("tk.tehtava_esim")} />
          <datalist id="tehtavat">{TEHTAVAT.map(([arvo, avain]) => <option key={arvo} value={arvo} label={t(avain)} />)}</datalist></label>
        <label style={{ ...kentta, flex: 1, minWidth: 120 }}>{t("tk.huomiot")}<input value={huomiot} onChange={(e) => setHuomiot(e.target.value)} /></label>
        <button className="nappi nappi--paa" disabled={tallentaa || !tunnit.trim()} onClick={tallenna}>{tallentaa ? "…" : t("pu.kirjaa")}</button>
      </div>
      {virhe && <p className="huom huom--vika" role="alert" style={{ marginTop: 6 }}>{virhe}</p>}
    </div>
  );
}

/* ── korvausluvut ─────────────────────────────────────────── */
function Asetukset({ onTallennettu }: { onTallennettu: () => void }) {
  const [a, setA] = useState<TuntiAsetukset | null>(null);
  useEffect(() => { api.tuntiAsetukset().then(setA).catch(() => {}); }, []);
  if (!a) return null;
  const kentta = (avain: keyof TuntiAsetukset, nimi: string, yks: string) => (
    <label>{nimi}
      <span><input type="number" step="0.01" min={0} defaultValue={a[avain]} style={{ width: 90 }}
        onBlur={(e) => api.paivitaTuntiAsetukset({ [avain]: Number(e.target.value) })} /> {yks}</span>
    </label>
  );
  return (
    <div style={{ background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end" }}>
        {kentta("km_korvaus", t("tk.km_korvaus_iso"), "€/km")}
        {kentta("paivaraha_koko", t("tk.pvraha.koko"), t("tk.eur_pv"))}
        {kentta("paivaraha_osa", t("tk.pvraha.osa"), t("tk.eur_pv"))}
        <button className="nappi nappi--kevyt" onClick={onTallennettu}>{t("tk.valmis")}</button>
      </div>
      <p className="dim" style={{ marginTop: 8 }}>
        {t("tk.asetukset_selite")}
      </p>
    </div>
  );
}
